import { useEffect, useRef, useState } from "react";
import { Check, HelpCircle, Server, Shield, Sparkles, Zap, RefreshCw, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import PricingQuiz from "./PricingQuiz";

interface PricingProps {
  isMobile?: boolean;
}

const Pricing = ({ isMobile: propIsMobile }: PricingProps = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const tiers = [
    {
      name: "Landing Page",
      tagline: "One-page presence for local businesses",
      price: "$1,000",
      features: [
        "Single page custom design",
        "Mobile-friendly",
        "Contact form",
        "Basic SEO",
        "SSL security",
        "Fast turnaround",
      ],
    },
    {
      name: "Growth Site",
      tagline: "Multi-page for growing businesses",
      price: "$2,500",
      popular: true,
      features: [
        "Up to 5 pages",
        "Custom design",
        "Advanced SEO",
        "Blog integration",
        "Lead forms",
        "Mobile optimized",
        "SSL included",
      ],
    },
    {
      name: "Brand & Scale",
      tagline: "Enterprise-level development",
      price: "$5,000",
      features: [
        "Unlimited pages",
        "E-commerce ready",
        "CRM integrations",
        "Custom functionality",
        "Priority support",
        "Dedicated mgmt",
        "Advanced analytics",
      ],
    },
  ];

  const managementFeatures = [
    { icon: Server, text: "Premium Hosting" },
    { icon: Shield, text: "SSL Security" },
    { icon: RefreshCw, text: "Daily Backups" },
    { icon: Sparkles, text: "Content Updates" },
  ];

  return (
    <section id="pricing" ref={sectionRef} className={`${propIsMobile ? "py-10" : "py-12 md:py-16"} relative overflow-hidden`}>
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/30 to-background" />

      <div className={`container mx-auto ${propIsMobile ? "px-3" : "px-4 md:px-6"} relative`}>
        {/* Header */}
        <div className={`text-center ${propIsMobile ? "mb-6" : "mb-8 md:mb-10"}`}>
          <p
            className={`text-primary font-medium tracking-widest uppercase ${propIsMobile ? "mb-2 text-xs" : "mb-3 text-sm"} transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Affordable Small Business Websites
          </p>
          <h2
            className={`${propIsMobile ? "text-xl" : "text-3xl md:text-5xl lg:text-6xl"} font-black mb-3 ${
              isVisible ? "animate-cinematic-fade-up" : "opacity-0"
            }`}
            style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
          >
            Custom Website <span className="text-gradient-static">Packages & Pricing</span>
          </h2>
          <p
            className={`${propIsMobile ? "text-xs" : "text-base md:text-lg"} text-muted-foreground max-w-2xl mx-auto mb-3 transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Ballpark estimates to help you plan. Exact quote after free consultation.
          </p>
          
          {/* Ballpark emphasis - mobile only */}
          {propIsMobile && (
            <p
              className={`text-[10px] text-muted-foreground italic max-w-xs mx-auto mb-4 transition-all duration-700 delay-250 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              These are ballpark numbers that adjust up or down depending on your needs. Your actual price will be given after consultation.
            </p>
          )}

          <button
            onClick={() => setIsQuizOpen(true)}
            className={`inline-flex items-center gap-1.5 ${propIsMobile ? "px-3 py-2 text-[10px]" : "px-5 py-2.5 text-sm"} bg-accent/20 border border-accent/30 text-accent font-bold rounded-full hover:bg-accent/30 transition-all duration-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "250ms" }}
          >
            <HelpCircle size={propIsMobile ? 12 : 18} />
            Not sure what you need? Take the quiz!
          </button>
        </div>

        <PricingQuiz isOpen={isQuizOpen} onClose={() => setIsQuizOpen(false)} />

        {/* Tier Cards */}
        <div className={`grid ${propIsMobile ? "grid-cols-2 gap-3" : "md:grid-cols-3 gap-5"} max-w-5xl mx-auto ${propIsMobile ? "mb-6" : "mb-8 md:mb-12"}`}>
          {tiers.map((tier, idx) => (
            <div
              key={idx}
              className={`group relative ${propIsMobile ? "p-3" : "p-6 md:p-8"} border-2 ${
                tier.popular
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card/50"
              } backdrop-blur-sm rounded-xl transition-all duration-300 ease-out hover:border-primary/70 hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_hsl(var(--primary)/0.3)] ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
              } ${propIsMobile && idx === 2 ? "col-span-2" : ""}`}
              style={{ transitionDelay: `${200 + idx * 100}ms` }}
            >
              {tier.popular && (
                <div className={`absolute ${propIsMobile ? "-top-2" : "-top-3"} left-1/2 -translate-x-1/2`}>
                  <span className={`${propIsMobile ? "px-2 py-0.5 text-[8px]" : "px-4 py-1 text-xs"} bg-primary text-primary-foreground font-bold uppercase rounded-full whitespace-nowrap`}>
                    Most Popular
                  </span>
                </div>
              )}

              <div className={tier.popular ? "mt-1" : ""}>
                <p className={`${propIsMobile ? "text-[8px]" : "text-xs"} text-muted-foreground uppercase tracking-wider mb-0.5`}>
                  Tier {idx + 1}
                </p>
                <h3 className={`${propIsMobile ? "text-sm" : "text-xl md:text-2xl"} font-black text-primary ${propIsMobile ? "mb-1" : "mb-2"}`}>
                  {tier.name}
                </h3>
                <p className={`${propIsMobile ? "text-[9px]" : "text-sm"} text-muted-foreground ${propIsMobile ? "mb-2" : "mb-4"}`}>{tier.tagline}</p>

                {/* Price */}
                <div className={`${propIsMobile ? "mb-3 p-2" : "mb-6 p-4"} bg-background/50 rounded-lg border border-border/50`}>
                  <p className={`${propIsMobile ? "text-[8px]" : "text-xs"} text-muted-foreground mb-0.5`}>Starting at</p>
                  <p className={`${propIsMobile ? "text-xl" : "text-3xl md:text-4xl"} font-black text-foreground`}>
                    {tier.price}
                  </p>
                </div>

                {/* Features - 2 columns on mobile */}
                <ul className={`${propIsMobile ? "grid grid-cols-2 gap-x-1 gap-y-1" : "space-y-3"}`}>
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <Check size={propIsMobile ? 10 : 16} className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span className={`${propIsMobile ? "text-[8px] leading-tight" : "text-sm"} text-muted-foreground`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div
          className={`text-center ${propIsMobile ? "mb-6" : "mb-10"} transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "500ms" }}
        >
          <p className={`${propIsMobile ? "text-[10px]" : "text-sm"} text-muted-foreground italic max-w-2xl mx-auto`}>
            All packages include basic SEO, mobile optimization, and SSL. 
            Fixed quote after consultation—no surprises.
          </p>
        </div>

        {/* Peace of Mind Management - Hidden on mobile */}
        {!propIsMobile && (
          <div
            className={`max-w-4xl mx-auto mb-8 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
            }`}
            style={{ transitionDelay: "600ms" }}
          >
            <div className="relative p-6 md:p-10 border-2 border-accent/40 bg-gradient-to-br from-accent/5 via-card/80 to-primary/5 rounded-xl overflow-hidden">
              <div className="absolute top-0 left-0 w-48 h-48 bg-accent/20 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 animate-pulse" />
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-primary/15 rounded-full blur-3xl translate-y-1/2 translate-x-1/2 animate-pulse" />

              <div className="relative">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/20 border border-accent/30 rounded-full mb-4">
                    <Zap size={16} className="text-accent" />
                    <span className="text-xs font-bold text-accent uppercase">Add-On Service</span>
                  </div>
                  <h3 className="text-2xl md:text-4xl font-black mb-3">
                    Peace of Mind <span className="text-gradient-static">Management</span>
                  </h3>
                  <p className="text-muted-foreground max-w-xl mx-auto">
                    Keep your website fast, secure, and always up-to-date.
                  </p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                  {managementFeatures.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 p-3 bg-background/50 rounded-lg border border-border/50"
                    >
                      <item.icon size={18} className="text-accent flex-shrink-0" />
                      <span className="text-sm font-medium text-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing Options */}
                <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {/* Monthly Option */}
                  <div className="p-5 bg-background/60 backdrop-blur-sm rounded-lg border border-border/50">
                    <p className="text-sm font-bold text-muted-foreground mb-2">Monthly</p>
                    <p className="text-3xl font-black text-foreground">
                      $75<span className="text-lg font-medium text-muted-foreground">/mo</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Flexible, cancel anytime</p>
                  </div>

                  {/* Yearly Option - Best Value */}
                  <div className="relative p-5 bg-background/60 backdrop-blur-sm rounded-lg border-2 border-green-500/50">
                    <div className="absolute -top-2.5 right-3">
                      <span className="px-3 py-0.5 bg-green-500 text-white text-xs font-bold uppercase rounded-full">
                        Save $300
                      </span>
                    </div>
                    <p className="text-sm font-bold text-green-500 mb-2">Yearly — Best Value</p>
                    <p className="text-3xl font-black text-foreground">
                      $600<span className="text-lg font-medium text-muted-foreground">/yr</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">That's only $50/month!</p>
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center mt-8">
                  <a
                    href="sms:+16317070672?body=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
                    className="inline-block px-8 py-3 bg-accent text-accent-foreground font-bold text-sm uppercase rounded transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
                  >
                    Add to Your Package
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Budget Section */}
        <div
          className={`max-w-2xl mx-auto transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
          }`}
          style={{ transitionDelay: "700ms" }}
        >
          <div className={`relative ${propIsMobile ? "p-4" : "p-6"} border-2 border-primary/50 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-xl text-center`}>
            <span className={`text-primary font-black ${propIsMobile ? "text-lg" : "text-xl md:text-2xl"}`}>Budget tight?</span>
            <span className={`text-foreground font-medium ${propIsMobile ? "text-sm" : "text-lg"}`}> Let's talk!</span>
            <p className={`${propIsMobile ? "text-[10px]" : "text-sm"} text-muted-foreground mt-1`}>
              Everyone deserves a great website. I'll work with you.
            </p>
            <a
              href="sms:+16317070672?body=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
              className={`inline-block mt-3 ${propIsMobile ? "px-4 py-2 text-[10px]" : "px-6 py-2.5 text-xs"} bg-primary text-primary-foreground font-bold uppercase rounded transition-all hover:scale-105`}
            >
              Start the Conversation
            </a>
          </div>
        </div>

        {/* Read More Link */}
        <div
          className={`text-center ${propIsMobile ? "mt-6" : "mt-10"} transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "800ms" }}
        >
          <Link
            to="/services-pricing"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline transition-all duration-300 group"
          >
            <span className={propIsMobile ? "text-sm" : "text-base"}>View Full Services & Pricing</span>
            <ArrowRight size={propIsMobile ? 16 : 18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Pricing;